import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { formatCurrency } from "@/components/StatusBadge";
import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Cell } from "recharts";

const BAR_COLORS = ["#FF7F00", "#F59E0B", "#EF4444", "#8B5CF6", "#3B82F6", "#10B981", "#EC4899", "#14B8A6"];

function BdOutstandingChart({ tree, selected, onBarClick }) {
  // Sum outstanding across all currencies per BD. Currency-mixing is only for
  // the *ranking* chart — each detail row below still shows currencies separately.
  const data = useMemo(() => {
    return (tree || []).map((bd) => {
      let total = 0;
      (bd.totals_by_currency || []).forEach((t) => { total += Number(t.outstanding || 0); });
      return { name: bd.bd_name || "Unassigned", total, bd_id: bd.bd_id };
    }).filter((r) => r.total > 0).sort((a, b) => b.total - a.total).slice(0, 10);
  }, [tree]);
  if (!data.length) return null;
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="bd-outstanding-chart">
      <div className="text-sm font-heading font-medium text-slate-800 mb-1">Top BDs by Outstanding (all currencies, indicative)</div>
      <div className="text-xs text-slate-500 mb-3">Click a bar to jump to that BD in the drill-down below.</div>
      <div style={{ width: "100%", height: 240 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 12, right: 16, left: 0, bottom: 40 }}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-25} textAnchor="end" />
            <YAxis tick={{ fontSize: 11 }} />
            <RTooltip formatter={(v) => new Intl.NumberFormat().format(Math.round(v))} />
            <Bar dataKey="total" radius={[4, 4, 0, 0]} onClick={(d) => onBarClick?.(d?.bd_id, d?.name)} className="cursor-pointer">
              {data.map((d, i) => (
                <Cell key={i}
                      fill={BAR_COLORS[i % BAR_COLORS.length]}
                      fillOpacity={selected && selected !== d.bd_id ? 0.35 : 1} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default function BdOutstanding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tree, setTree] = useState([]);
  const [expandedBd, setExpandedBd] = useState({});
  const [expandedClient, setExpandedClient] = useState({});
  const [expandedProject, setExpandedProject] = useState({});
  const [search, setSearch] = useState("");
  const isBd = user?.role === "business_development";

  useEffect(() => {
    api.get("/api/reports/bd-outstanding-drilldown").then((r) => {
      setTree(r.data || []);
      // Auto-expand the only BD (typical BD user view)
      if (isBd && r.data?.length === 1) {
        setExpandedBd({ [r.data[0].bd_id || "unassigned"]: true });
      }
    });
  }, [isBd]);

  const s = search.trim().toLowerCase();
  const filterMatch = (label) => !s || (label || "").toLowerCase().includes(s);

  return (
    <div className="space-y-6" data-testid="bd-outstanding-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight">
            {isBd ? "My Outstanding" : "BD Outstanding"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {isBd
              ? "All open invoices attributed to you — grouped by Client → Project → Invoice, split by currency."
              : "Every Business Development owner's open invoices — expand to drill into Client → Project → Invoice. Currencies are shown separately, never combined."}
          </p>
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search client, project, invoice…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-8 w-72"
            data-testid="bd-outstanding-search"
          />
        </div>
      </div>

      {tree.length === 0 && (
        <Card className="border-slate-200 shadow-none p-8 text-center text-slate-500">
          No outstanding — every invoice is fully paid. 🎉
        </Card>
      )}

      {tree.length > 0 && !isBd && <BdOutstandingChart
        tree={tree}
        selected={Object.keys(expandedBd).find((k) => expandedBd[k])}
        onBarClick={(bdId, name) => {
          // Expand the BD section and scroll to it — drill-through from chart
          setExpandedBd({ [bdId || "unassigned"]: true });
          setTimeout(() => {
            const el = document.querySelector(`[data-bd-section="${bdId || "unassigned"}"]`);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        }}
      />}

      {tree.map((bd) => {
        const bdKey = bd.bd_id || "unassigned";
        const isOpen = expandedBd[bdKey] ?? false;
        // Filter clients by search
        const clientsF = (bd.clients || []).filter((c) =>
          filterMatch(c.client_name) ||
          (c.projects || []).some((p) => filterMatch(p.project_name) || filterMatch(p.project_code) ||
            (p.invoices || []).some((iv) => filterMatch(iv.invoice_number)))
        );
        if (s && clientsF.length === 0) return null;

        return (
          <Card key={bdKey} className="border-slate-200 shadow-none p-4" data-testid={`bd-card-${bdKey}`} data-bd-section={bdKey}>
            <button
              className="w-full text-left flex items-start justify-between gap-4 mb-2"
              onClick={() => setExpandedBd((e) => ({ ...e, [bdKey]: !isOpen }))}
              data-testid={`bd-toggle-${bdKey}`}
            >
              <div className="flex items-center gap-2">
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-heading font-medium text-lg">{bd.bd_name}</span>
                <span className="text-xs text-slate-500">({clientsF.length} client{clientsF.length === 1 ? "" : "s"})</span>
              </div>
              <div className="text-right text-sm flex flex-wrap gap-x-4 gap-y-1 justify-end">
                {(bd.totals_by_currency || []).map((t) => (
                  <span key={t.currency}>
                    <span className="text-slate-500 text-xs">Outstanding: </span>
                    <span className="font-semibold text-amber-700 tabular-nums">{formatCurrency(t.outstanding, t.currency)}</span>
                  </span>
                ))}
              </div>
            </button>

            {isOpen && (
              <div className="mt-3 space-y-2">
                {clientsF.map((c) => {
                  const cKey = `${bdKey}::${c.client_id}::${c.currency}`;
                  const cOpen = expandedClient[cKey] ?? false;
                  return (
                    <div key={cKey} className="border border-slate-200 rounded-lg" data-testid={`client-row-${c.client_id}`}>
                      <button
                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-50"
                        onClick={() => setExpandedClient((e) => ({ ...e, [cKey]: !cOpen }))}
                        data-testid={`client-toggle-${c.client_id}`}
                      >
                        <div className="flex items-center gap-2 text-left">
                          {cOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          <span className="font-medium">{c.client_name}</span>
                          <span className="text-xs text-slate-500">({c.projects.length} project{c.projects.length === 1 ? "" : "s"})</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-slate-500 text-xs mr-1">{c.currency}</span>
                          <span className="font-semibold text-amber-700 tabular-nums">{formatCurrency(c.total_outstanding, c.currency)}</span>
                        </div>
                      </button>

                      {cOpen && (
                        <div className="border-t border-slate-100 divide-y divide-slate-100">
                          {c.projects.map((p) => {
                            const pKey = `${cKey}::${p.project_id}`;
                            const pOpen = expandedProject[pKey] ?? false;
                            return (
                              <div key={pKey} className="bg-slate-50/40" data-testid={`project-row-${p.project_id}`}>
                                <button
                                  className="w-full flex items-center justify-between px-6 py-2 hover:bg-slate-50"
                                  onClick={() => setExpandedProject((e) => ({ ...e, [pKey]: !pOpen }))}
                                  data-testid={`project-toggle-${p.project_id}`}
                                >
                                  <div className="flex items-center gap-2 text-left">
                                    {pOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                                    <span className="font-mono text-xs text-slate-500">{p.project_code || "—"}</span>
                                    <span className="text-sm">{p.project_name}</span>
                                    <span className="text-xs text-slate-500">({p.invoices.length} invoice{p.invoices.length === 1 ? "" : "s"})</span>
                                  </div>
                                  <div className="text-sm">
                                    <span className="font-semibold text-amber-700 tabular-nums">{formatCurrency(p.total_outstanding, c.currency)}</span>
                                  </div>
                                </button>

                                {pOpen && (
                                  <div className="px-6 pb-3">
                                    <table className="data-table w-full text-xs">
                                      <thead>
                                        <tr>
                                          <th>Invoice #</th><th>Invoice Date</th><th>Due Date</th>
                                          <th className="text-right">Amount</th>
                                          <th className="text-right">Received</th>
                                          <th className="text-right">Outstanding</th>
                                          <th>Status</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {p.invoices.map((iv) => (
                                          <tr key={iv.invoice_id} className="cursor-pointer hover:bg-white" onClick={() => navigate("/invoices")} data-testid={`invoice-row-${iv.invoice_id}`}>
                                            <td className="font-mono">{iv.invoice_number}</td>
                                            <td>{iv.invoice_date?.slice(0, 10)}</td>
                                            <td>{iv.due_date?.slice(0, 10)}</td>
                                            <td className="text-right tabular-nums">{formatCurrency(iv.invoice_amount, c.currency)}</td>
                                            <td className="text-right tabular-nums text-emerald-700">{formatCurrency(iv.amount_received, c.currency)}</td>
                                            <td className="text-right tabular-nums text-amber-700 font-medium">{formatCurrency(iv.outstanding_amount, c.currency)}</td>
                                            <td className="capitalize">{iv.payment_status?.replace(/_/g, " ")}</td>
                                          </tr>
                                        ))}
                                        {p.unbilled_amount > 0 && (
                                          <tr className="bg-sky-50/60" data-testid={`unbilled-row-${p.project_id}`}>
                                            <td colSpan={5} className="italic text-slate-600">
                                              Unbilled scope (Change Order / Add-line not yet invoiced)
                                            </td>
                                            <td className="text-right tabular-nums text-sky-800 font-medium">{formatCurrency(p.unbilled_amount, c.currency)}</td>
                                            <td className="text-sky-700">to be invoiced</td>
                                          </tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
